import { __ } from '@wordpress/i18n';
import {
    Card,
    DokanToaster,
    SearchableSelect,
    useToast,
} from '@getdokan/dokan-ui';
import ChatItem from './ChatItem';
import { DokanButton, DateTimeHtml, RichText } from '@dokan/components';
import CloseLogItem from './CloseLogItem';
import OpenLogItem from './OpenLogItem';
import { useState, useEffect, useRef } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { Ticket, TicketConversation, TicketStatusLog } from '../type/Ticket';
import { ChevronLeft } from 'lucide-react';
import Skeleton from './Skeleton';

// A combined type to handle both conversations and logs in a single sorted timeline
type TimelineItem =
    | ( TicketConversation & { itemType: 'conversation' } )
    | ( TicketStatusLog & { itemType: 'log' } );

function Details( { params, navigate, context = 'admin' } ) {
    const { id } = params;
    const [ ticket, setTicket ] = useState< Ticket | null >( null );
    const [ timeline, setTimeline ] = useState< TimelineItem[] >( [] );
    const [ isLoading, setIsLoading ] = useState( true );
    const [ error, setError ] = useState< string | null >( null );
    const [ reply, setReply ] = useState( '' );
    const [ isReplying, setIsReplying ] = useState( false );
    const [ newStatus, setNewStatus ] = useState( '' );
    const toast = useToast();
    const [ isUpdatingStatus, setIsUpdatingStatus ] = useState( false );
    const timelineRef = useRef< HTMLDivElement >( null );
    const [ statusOptions, setStatusOptions ] = useState( [
        {
            label: __( 'Active', 'dokan' ),
            value: 'open',
        },
        {
            label: __( 'Close', 'dokan' ),
            value: 'closed',
        },
    ] );

    /**
     * Fetches ticket data from the API.
     */
    const fetchTicket = async () => {
        setError( null );

        try {
            const response = await apiFetch< Ticket >( {
                path: `/dokan/v1/vendor-support/tickets/${ id }`,
            } );
            setTicket( response );

            const combinedTimeline = [
                ...( response.conversations || [] ).map( ( c ) => ( {
                    ...c,
                    itemType: 'conversation' as const,
                } ) ),
                ...( response.status_logs || [] ).map( ( l ) => ( {
                    ...l,
                    itemType: 'log' as const,
                } ) ),
            ];

            combinedTimeline.sort(
                ( a, b ) =>
                    new Date( a.date_created ).getTime() -
                    new Date( b.date_created ).getTime()
            );

            setTimeline( combinedTimeline );
        } catch ( e: any ) {
            setError(
                e.message ||
                    __(
                        'An error occurred while fetching the ticket details.',
                        'dokan'
                    )
            );
        } finally {
            if ( isLoading ) {
                setIsLoading( false );
            }
        }
    };

    const setFormatedStatus = ( status ) => {
        const data = statusOptions.find( ( item ) => {
            return item.value === status;
        } );

        setNewStatus( data );
    };

    /**
     * Handles the submission of a new reply.
     */
    const handleReplySubmit = async () => {
        if ( ! reply.trim() || isReplying ) {
            return;
        }

        setIsReplying( true );

        try {
            await apiFetch( {
                path: `/dokan/v1/vendor-support/tickets/${ id }/conversations`,
                method: 'POST',
                data: { message: reply },
            } );
            setReply( '' );
            await fetchTicket();

            toast( {
                type: 'success',
                title: __( 'Replied successfully.', 'dokan' ),
            } );
        } catch ( e: any ) {
            // You could show an error notification to the user here.
            toast( {
                type: 'error',
                title: e?.message || __( 'Failed to submit reply.', 'dokan' ),
            } );
        } finally {
            setIsReplying( false );
        }
    };

    /**
     * Handles updating the ticket's status.
     */
    const handleStatusUpdate = async () => {
        if ( ! newStatus || newStatus === ticket?.status || isUpdatingStatus ) {
            return;
        }

        setIsUpdatingStatus( true );

        try {
            await apiFetch( {
                path: `/dokan/v1/vendor-support/tickets/${ id }`,
                method: 'POST',
                data: { status: newStatus?.value },
            } );
            // Refetch the ticket data to show the updated status and new status log.
            await fetchTicket();
            toast( {
                type: 'success',
                title: __( 'Status updated successfully.', 'dokan' ),
            } );
        } catch ( e: any ) {
            toast( {
                type: 'error',
                title: e?.message || __( 'Failed to update status', 'dokan' ),
            } );
            // You could show an error notification to the user here.
        } finally {
            setIsUpdatingStatus( false );
        }
    };

    const body = () => {
        if ( isLoading || ! ticket || error ) {
            return <Skeleton />;
        }

        return (
            <div className="flex flex-col-reverse md:flex-row md:justify-between gap-4">
                <div className="md:w-3/4">
                    <Card className="bg-white rounded-md shadow-md">
                        <div className="border-b-2 p-4">
                            <h2 className="font-bold text-lg">
                                #{ id } - { ticket.subject }
                            </h2>
                        </div>

                        <div
                            ref={ timelineRef }
                            className="flex flex-col w-full h-[600px] overflow-auto scroll-smooth border-b-2"
                        >
                            { timeline.map( ( item ) => {
                                if ( item.itemType === 'conversation' ) {
                                    return (
                                        <ChatItem
                                            key={ `conv-${ item.id }` }
                                            conversation={ item }
                                        />
                                    );
                                }
                                if ( item.itemType === 'log' ) {
                                    if ( item.new_status === 'closed' ) {
                                        return (
                                            <CloseLogItem
                                                key={ `log-${ item.id }` }
                                                log={ item }
                                            />
                                        );
                                    }
                                    if ( item.new_status === 'open' ) {
                                        return (
                                            <OpenLogItem
                                                key={ `log-${ item.id }` }
                                                log={ item }
                                            />
                                        );
                                    }
                                }
                                return null;
                            } ) }
                        </div>

                        <div className="p-4 flex flex-col">
                            <RichText
                                value={ reply }
                                onChange={ setReply }
                                placeholder={ __(
                                    'Enter your reply…',
                                    'dokan'
                                ) }
                                readOnly={ isReplying }
                            />

                            <div className="text-end">
                                <DokanButton
                                    className="mt-2"
                                    onClick={ handleReplySubmit }
                                    disabled={ isReplying || ! reply.trim() }
                                    label={__( 'Reply', 'dokan' )}
                                    loading={isReplying}
                                />
                            </div>
                        </div>
                    </Card>
                </div>
                <div className="md:w-1/4">
                    <Card className="bg-white rounded-md shadow-md">
                        { context === 'admin' && (
                            <>
                                <div className="border-b w-full h-auto p-4 flex flex-col justify-center items-center gap-4">
                                    <div className="w-[106px] h-[106px] rounded-full overflow-hidden">
                                        <img
                                            src={ ticket?.vendor?.gravatar }
                                            alt="vendor-avatar"
                                        />
                                    </div>
                                    <h2 className="font-bold text-lg">
                                        { ticket?.vendor?.store_name }
                                    </h2>
                                </div>
                                <div className="border-b p-4 flex flex-col gap-2">
                                    <span className="flex flex-row">
                                        <span className="font-semibold">
                                            { __( 'Email:', 'dokan' ) }
                                        </span>
                                        <span className="ml-1">
                                            { ticket?.vendor?.email ?? '' }
                                        </span>
                                    </span>

                                    <span className="flex flex-row">
                                        <span className="font-semibold">
                                            { __( 'Conversations:', 'dokan' ) }
                                        </span>
                                        <span className="ml-1">
                                            { ticket.conversations_count }
                                        </span>
                                    </span>

                                    <span className="flex flex-row">
                                        <span className="font-semibold">
                                            { __( 'Last update:', 'dokan' ) }
                                        </span>
                                        <span className="ml-1">
                                            <DateTimeHtml
                                                date={ ticket?.date_updated }
                                            />
                                        </span>
                                    </span>
                                </div>
                            </>
                        ) }
                        <div className="flex flex-col gap-4 items-end p-4">
                            <div className="w-full">
                                <SearchableSelect
                                    placeholder={ __(
                                        'Change Status',
                                        'dokan'
                                    ) }
                                    value={ newStatus }
                                    onChange={ ( option ) =>
                                        setFormatedStatus( option.value )
                                    }
                                    options={ statusOptions }
                                />
                            </div>
                            <DokanButton
                                className="w-1/2"
                                onClick={ handleStatusUpdate }
                                disabled={
                                    isUpdatingStatus ||
                                    ! newStatus ||
                                    newStatus === ticket.status
                                }
                                loading={ isUpdatingStatus }
                                label={__( 'Update', 'dokan' )}
                            />
                        </div>
                    </Card>
                </div>
            </div>
        );
    };

    // Effect to set the initial status in the dropdown once the ticket loads.
    useEffect( () => {
        if ( ticket ) {
            setFormatedStatus( ticket.status );
        }
    }, [ ticket ] );

    // Effect for the initial data fetch.
    useEffect( () => {
        if ( id ) {
            setIsLoading( true );
            fetchTicket();
        }
    }, [ id ] );

    // Effect to scroll the timeline to the bottom when it updates.
    useEffect( () => {
        if ( timelineRef.current ) {
            timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
        }
    }, [ timeline ] );

    return (
        <div>
            { context === 'admin' && (
                <h1>{ __( 'Vendor Support', 'dokan' ) }</h1>
            ) }

            <DokanButton
                onClick={ () => {
                    navigate( `/vendor-support` );
                } }
                variant="secondary"
                className="h-8 mb-6 mt-4"
            >
                <ChevronLeft className="-ml-3" />
                { __( 'Back to List', 'dokan' ) }
            </DokanButton>

            { body() }

            <DokanToaster />
        </div>
    );
}

export default Details;
